'use client';

import { useEffect, useState } from 'react';

import { cn } from '@/shared/utils/cn';

interface CourseCardPromoBarProps {
  label: string;
  /** Fecha límite en ISO 8601. Si es futura, se muestra un contador regresivo. */
  deadline?: string;
  className?: string;
}

interface Remaining {
  diff: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getRemaining(target: number): Remaining {
  const diff = Math.max(0, target - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  return {
    diff,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

const pad = (value: number) => String(value).padStart(2, '0');

export function CourseCardPromoBar({ label, deadline, className }: CourseCardPromoBarProps) {
  const target = deadline ? new Date(deadline).getTime() : NaN;
  const hasCountdown = Number.isFinite(target);
  // Inicia en null para no desincronizar SSR/cliente; el contador se llena al montar.
  const [remaining, setRemaining] = useState<Remaining | null>(null);

  useEffect(() => {
    if (!hasCountdown) return;
    setRemaining(getRemaining(target));
    const intervalId = window.setInterval(() => setRemaining(getRemaining(target)), 1000);
    return () => window.clearInterval(intervalId);
  }, [hasCountdown, target]);

  if (hasCountdown && remaining && remaining.diff <= 0) return null;

  return (
    <div
      className={cn(
        'bg-(--color-primary-500) z-10 flex flex-wrap items-center justify-center gap-x-2 px-3 py-1.5 text-center text-[0.64rem] font-bold uppercase leading-tight tracking-wide text-adipa-primary-foreground',
        className,
      )}
    >
      <span>{label}</span>
      {remaining ? (
        <span className="tracking-normal tabular-nums">
          {pad(remaining.days)}D : {pad(remaining.hours)}H : {pad(remaining.minutes)}M :{' '}
          {pad(remaining.seconds)}S
        </span>
      ) : null}
    </div>
  );
}
